import { useContext, useEffect, useState, ReactNode, useCallback, useRef, useMemo } from 'react'
import type { Session } from '../lib/gotrueTypes'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../utils/supabase'
import { setAuthTokenGetter, setCachedAuthToken, setUnauthorizedHandler } from '../api/client'
import {
  getAccountStatus,
  AccountStatus,
  tryParseSoftDeleteFromResponse,
  tryParseInvalidSessionFromResponse,
} from '../api/auth'
import { createDebug } from '../utils/debugLog'
import i18n from '../i18n/i18n'
import { normalizeLocale } from '../i18n/supportedLocales'
import { AuthContext, type AuthContextValue } from './authContextRaw'
import { analyticsOnSignOut, trackSignedIn, type AuthMethod } from '../analytics/events'
import { dropOtherUserBuckets } from '../utils/adminApiKeyStore'

const log = createDebug('dw:account-self-service:auth-context')
const logInvalidSession = createDebug('dw:auth-session-invalid')

/** Map GoTrue's provider to a provider-safe analytics method (RFC-0974 §7.2). */
function providerToMethod(provider: string | undefined): AuthMethod {
  return provider === 'google' || provider === 'facebook' || provider === 'email'
    ? provider
    : 'email'
}

// RFC-0974: OAuth logins return via a full-page redirect. We stash the provider
// in sessionStorage BEFORE redirecting so the AuthProvider can, on return,
// distinguish a completed OAuth login from an ordinary session restore and emit
// `auth_signed_in` for it. sessionStorage survives the redirect (same origin).
const OAUTH_PENDING_KEY = 'dw_oauth_pending'

function setOAuthPending(provider: 'google' | 'facebook'): void {
  try {
    sessionStorage.setItem(OAUTH_PENDING_KEY, provider)
  } catch {
    /* best-effort — analytics must never break sign-in */
  }
}

/** Read + clear the OAuth-pending flag; returns the provider iff one was set. */
function consumeOAuthPending(): AuthMethod | null {
  try {
    const value = sessionStorage.getItem(OAUTH_PENDING_KEY)
    if (value) {
      sessionStorage.removeItem(OAUTH_PENDING_KEY)
      if (value === 'google' || value === 'facebook') return value
    }
  } catch {
    /* best-effort */
  }
  return null
}

function clearOAuthPending(): void {
  try {
    sessionStorage.removeItem(OAUTH_PENDING_KEY)
  } catch {
    /* best-effort */
  }
}

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [softDeleteStatus, setSoftDeleteStatus] = useState<AccountStatus | null>(null)
  const queryClient = useQueryClient()
  const previousUserIdRef = useRef<string | null>(null)
  // RFC-0974: emit `auth_signed_in` ONLY on a genuine no-session→session
  // transition, so a silent token refresh or an initial session restore is not
  // miscounted as a sign-in.
  const hadSessionRef = useRef(false)
  // Guards the invalid-session sign-out so a burst of concurrent 401s (the
  // ~20s credits poll plus any in-flight requests) triggers exactly one
  // signOut() + redirect, not a flurry of them.
  const invalidSessionHandledRef = useRef(false)

  const getAccessToken = useCallback(async () => {
    const { data: { session: current } } = await supabase.auth.getSession()
    if (!current) return null
    if (current.expires_at && Date.now() / 1000 >= current.expires_at) {
      const { data: { session: refreshed } } = await supabase.auth.refreshSession()
      return refreshed?.access_token ?? null
    }
    return current.access_token
  }, [])

  useEffect(() => {
    // RFC-1188: whenever a session is established, drop every /admin/api raw-key
    // bucket that does not belong to it. This MUST live here, on the sign-in
    // boundary, and not in ApiKeysSection — that component only mounts on
    // /admin/api, so admin A's raw `dwk_` values would otherwise survive
    // indefinitely if admin B signed in on the same browser profile without ever
    // visiting the page. Nothing else clears app localStorage on sign-out.
    const scopeAdminApiKeysToSession = (current: Session | null) => {
      const userId = current?.user?.id
      if (userId) dropOtherUserBuckets(userId)
    }

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      scopeAdminApiKeysToSession(session)
      setIsLoading(false)
      hadSessionRef.current = !!session
      // RFC-0974: an OAuth (Google/Facebook) login returns via a full-page
      // redirect, so its SIGNED_IN fired before this subscription existed —
      // supabase-js reports the restored session as INITIAL_SESSION. The
      // redirect-completion flag (set before signInWithOAuth) distinguishes a
      // completed OAuth login from an ordinary session restore. ALWAYS consume
      // the marker on load — including a cancelled / errored redirect that
      // returns WITHOUT a session — so a stale marker can't misattribute a
      // later email/password sign-in in the same tab. Emit only if a session
      // actually materialized.
      const oauthMethod = consumeOAuthPending()
      if (session && oauthMethod) trackSignedIn({ method: oauthMethod })
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const hadSession = hadSessionRef.current
        setSession(session)
        scopeAdminApiKeysToSession(session)
        hadSessionRef.current = !!session
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          queryClient.invalidateQueries()
        }
        // RFC-0974 auth_signed_in: emit exactly once per genuine sign-in.
        //  - OAuth redirect login → matched by the redirect-completion flag
        //    (may arrive as INITIAL_SESSION, not SIGNED_IN).
        //  - Email/password login → a no-session→session `SIGNED_IN`
        //    (INITIAL_SESSION restore + TOKEN_REFRESHED are excluded; the
        //    `!hadSession` guard also covers any version reusing SIGNED_IN for a
        //    restore). `consumeOAuthPending` clears the flag so the two paths
        //    never double-emit.
        if (session) {
          const oauthMethod = consumeOAuthPending()
          if (oauthMethod) {
            trackSignedIn({ method: oauthMethod })
          } else if (event === 'SIGNED_IN' && !hadSession) {
            trackSignedIn({ method: providerToMethod(session.user?.app_metadata?.provider) })
          }
        }
        if (event === 'SIGNED_OUT') {
          // Reset soft-delete state — the next signed-in user starts fresh.
          setSoftDeleteStatus(null)
          // RFC-0974: end the analytics session so a next sign-in in this tab
          // starts a fresh session + re-stitches (funnels don't span users).
          analyticsOnSignOut()
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [queryClient])

  useEffect(() => {
    setAuthTokenGetter(getAccessToken)
  }, [getAccessToken])

  useEffect(() => {
    setCachedAuthToken(session?.access_token ?? null)
  }, [session])

  // Account-self-service: install a global 403 handler so any request that
  // returns ``{"detail": {"restore_required": true, ...}}`` flips the
  // softDeleteStatus state and lets the router redirect to /account/restore.
  useEffect(() => {
    setUnauthorizedHandler(async (res: Response) => {
      // (1) Soft-delete restore (403 with restore_required) — unchanged.
      const payload = await tryParseSoftDeleteFromResponse(res)
      if (payload) {
        log('403 with restore_required — flipping isSoftDeleted')
        setSoftDeleteStatus({
          soft_deleted: true,
          scheduled_purge_at: payload.scheduledPurgeAt,
          soft_deleted_at: null,
          days_remaining: null,
        })
        // Do not navigate from here directly — components react to
        // softDeleteStatus and handle their own routing.  The
        // AccountRestorePage is the canonical landing.
        if (typeof window !== 'undefined' && window.location.pathname !== '/account/restore') {
          window.location.assign('/account/restore')
        }
        return
      }

      // (2) Invalid session (401 with code AUTH_SESSION_INVALID) — a cross-env /
      // orphan-``sub`` token this environment rejected (RFC-0851). Sign out to
      // clear the stale refresh token (breaks the bad-``sub`` refresh loop) and
      // route to /login for a fresh sign-in. Ref-guarded so it runs once.
      const isInvalidSession = await tryParseInvalidSessionFromResponse(res)
      if (isInvalidSession && !invalidSessionHandledRef.current) {
        invalidSessionHandledRef.current = true
        logInvalidSession('401 AUTH_SESSION_INVALID — signing out and redirecting to /login')
        try {
          await supabase.auth.signOut()
        } catch {
          /* sign-out best-effort — still redirect so the user can re-auth */
        }
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.assign('/login')
        }
      }
    })
    return () => setUnauthorizedHandler(null)
  }, [])

  // After sign-in, fetch /auth/account/status once.  If the user is
  // soft-deleted, redirect to /account/restore.
  useEffect(() => {
    if (!session?.user?.id) {
      setSoftDeleteStatus(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const status = await getAccountStatus()
        if (cancelled) return
        log('account status fetched soft_deleted=%s', status.soft_deleted)
        setSoftDeleteStatus(status)
        if (status.soft_deleted && typeof window !== 'undefined') {
          if (window.location.pathname !== '/account/restore') {
            window.location.assign('/account/restore')
          }
        }
      } catch (err) {
        log('account status fetch failed %o', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [session?.user?.id])

  useEffect(() => {
    const currentUserId = session?.user?.id ?? null
    if (previousUserIdRef.current !== currentUserId && previousUserIdRef.current !== null) {
      queryClient.invalidateQueries()
    }
    previousUserIdRef.current = currentUserId
  }, [session?.user?.id, queryClient])

  const signInWithGoogle = async () => {
    const redirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL || window.location.origin
    setOAuthPending('google') // RFC-0974: mark so the redirect return emits auth_signed_in
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
      },
    })
    // Setup failed before navigation — clear the marker so a later email login /
    // session restore in this tab doesn't consume a stale one (RFC-0974).
    if (error) {
      clearOAuthPending()
      throw error
    }
  }

  const signInWithFacebook = async () => {
    const redirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL || window.location.origin
    setOAuthPending('facebook') // RFC-0974: mark so the redirect return emits auth_signed_in
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'facebook',
      options: {
        redirectTo: redirectUrl,
      },
    })
    if (error) {
      clearOAuthPending()
      throw error
    }
  }

  const signInWithMagicLink = async (email: string) => {
    const redirectUrl = import.meta.env.VITE_AUTH_REDIRECT_URL || window.location.origin
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: redirectUrl,
        // Seed the display-only email-render locale for a NEW magic-link user so
        // the OTP/magic-link email is localized (mirrors signup). For an EXISTING
        // user GoTrue ignores this and uses their stored user_metadata. Never
        // authz (S1-1); source of truth stays public.user_preferred_locales.
        data: {
          email_locale: normalizeLocale(i18n.language) ?? 'en',
        },
      },
    })
    return { error: error ? new Error(error.message) : null }
  }

  const signOut = async () => {
    await supabase.auth.signOut()
  }

  const hasPasswordIdentity = useMemo(() => {
    const identities = session?.user?.identities
    if (!Array.isArray(identities)) return false
    return identities.some((i) => i.provider === 'email')
  }, [session?.user?.identities])

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    isLoading,
    isAuthenticated: !!session,
    signInWithGoogle,
    signInWithFacebook,
    signInWithMagicLink,
    signOut,
    hasPasswordIdentity,
    isSoftDeleted: softDeleteStatus?.soft_deleted ?? false,
    softDeleteStatus,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
